import { type TypedDocumentNode, gql } from '@apollo/client';

import type { FiatCurrency } from '#src/__generated__/globalTypes.ts';
import { withFragments } from '#src/lib/gql/index.ts';
import { monetaryAmountFragment } from '#src/lib/monetaryAmount.ts';

import type { useStoreAmount_primaryOffer } from './__generated__/useStoreAmount.graphql.ts';
import { useAmountWithConversion } from './useAmountWithConversion.ts';
import { useCurrentUserCurrencies } from './useCurrentUserCurrencies.tsx';
import { useIsMobileApp } from './useIsMobileApp.ts';

const symbolRx: Record<FiatCurrency, RegExp> = {
  EUR: /(^|[0-9]|\s)€($|[0-9]|\s)/,
  USD: /(^|[0-9]|\s)\$($|[0-9]|\s)/,
  GBP: /(^|[0-9]|\s)£($|[0-9]|\s)/,
};

export const useStoreAmount = withFragments(
  (primaryOffer: Nullable<useStoreAmount_primaryOffer>) => {
    const { price, googlePrice, applePrice } = primaryOffer || {};
    const { isAndroidApp, isIosApp, isMarketAuthorizedIosApp } =
      useIsMobileApp();
    const { fiatCurrency } = useCurrentUserCurrencies();
    const { main } = useAmountWithConversion({ monetaryAmount: price });
    const { main: mainApplePrice, exponent: mainAppleExponent } =
      useAmountWithConversion({
        monetaryAmount: applePrice,
      });

    if (isAndroidApp && googlePrice) {
      const samePriceOrSameCurrency =
        main === googlePrice || googlePrice.match(symbolRx[fiatCurrency]);

      const storeMain = samePriceOrSameCurrency ? googlePrice : main;
      const storeExponent = samePriceOrSameCurrency ? undefined : googlePrice;
      return { storeMain, storeExponent };
    }
    if (isIosApp && !isMarketAuthorizedIosApp && applePrice) {
      return { storeMain: mainApplePrice, storeExponent: mainAppleExponent };
    }
    return { storeMain: undefined, storeExponent: undefined };
  },
  {
    primaryOffer: gql`
      fragment useStoreAmount_primaryOffer on TokenPrimaryOffer {
        id
        price {
          ...MonetaryAmountFragment_monetaryAmount
        }
        googlePrice(countryCode: $googlePlayStoreCountryCode)
          @include(if: $isAndroidApp)
        applePrice @include(if: $isIosApp) {
          ...MonetaryAmountFragment_monetaryAmount
        }
      }
      ${monetaryAmountFragment}
    ` as TypedDocumentNode<useStoreAmount_primaryOffer>,
  }
);
